import React, { useEffect, useState } from "react";
import { Container, Row, Col, Form } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { post } from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import CustomInput from "../../../components/AuthenticationComponents/CustomInput";
import CustomSelector from "../../../components/AuthenticationComponents/CustomSelector";
import SecondaryButton from "../../../components/Common/SecondaryButton";
import tickIcon from "../../../assets/images/support/tick-circle-white.svg";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import { extractErrorMessage } from "../../../utils/errorHandler";
import cancelIcon from "../../../assets/images/user-dashboard/remove-circle.svg";

/* -------------------------------------------------------------------------
 * AdminPointsRedemptionTierCreatePage — create/edit form for a single points redemption tier.
 * ---------------------------------------------------------------------- */

const AdminPointsRedemptionTierCreatePage = ({ mode = "create" }) => {
  document.title = mode === "create" ? "Create Points Redemption Tier | Omaha Food Lovers" : "Edit Points Redemption Tier | Omaha Food Lovers";
  const navigate = useNavigate();
  const location = useLocation();
  const { tierId } = useParams();
  const [loading, setLoading] = useState(false);

  toastr.options = {
    positionClass: "toast-bottom-left",
    timeOut: 5000,
    closeButton: true,
    progressBar: true,
  };

  // Populate form from navigation state for edit mode
  useEffect(() => {
    if (mode === "edit" && tierId) {
      const stateTierData = location.state?.tierData;
      if (stateTierData) {
        // Use the data passed from the list page
        formik.setValues({
          name: stateTierData.name || "",
          amount: (stateTierData.amount_cents / 100).toFixed(2) || "",
          points_required: stateTierData.points_required || "",
          is_active: stateTierData.is_active !== false ? "true" : "false",
          sort_order: stateTierData.sort_order || "",
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, tierId]);

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .required("Name is required")
      .min(2, "Name must be at least 2 characters")
      .max(100, "Name must not exceed 100 characters"),
    amount: Yup.string()
      .required("Amount is required")
      .matches(/^\d+(\.\d{1,2})?$/, "Please enter a valid amount (e.g., 10.00)")
      .test("min-amount", "Amount must be at least $0.01", (value) => parseFloat(value) > 0),
    points_required: Yup.number()
      .required("Points required is required")
      .min(1, "Points required must be at least 1")
      .integer("Points required must be a whole number")
      .typeError("Points required must be a number"),
    is_active: Yup.string().required("Status is required"),
    sort_order: Yup.number()
      .required("Sort order is required")
      .min(0, "Sort order must be at least 0")
      .integer("Sort order must be a whole number")
      .typeError("Sort order must be a number"),
  });

  const formik = useFormik({
    initialValues: {
      name: "",
      amount: "",
      points_required: "",
      is_active: "true",
      sort_order: "",
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setTouched, validateForm }) => {
      setTouched({
        name: true,
        amount: true,
        points_required: true,
        is_active: true,
        sort_order: true,
      });

      const errors = await validateForm();
      if (Object.keys(errors).length > 0) return;

      setLoading(true);

      try {
        const payload = {
          name: values.name,
          amount_cents: Math.round(parseFloat(values.amount) * 100),
          points_required: parseInt(values.points_required),
          is_active: values.is_active === "true",
          sort_order: parseInt(values.sort_order),
        };

        let response;
        if (mode === "create") {
          response = await post(endpoints.adminPointsRedemptionTiers, payload);
        } else if (mode === "edit" && tierId) {
          const endpoint = endpoints.adminPointsRedemptionTier.replace("{{id}}", tierId);
          response = await post(endpoint, payload);
        }

        if (response?.data?.status || response?.data?.success) {
          toastr.success(
            mode === "create" ? "Points redemption tier created successfully" : "Points redemption tier updated successfully"
          );
          navigate("/admin/points-redemption-tiers");
        }
      } catch (err) {
        console.error("Failed to save points redemption tier:", err);
        if (err.response?.data?.errors) {
          const validationErrors = Object.values(err.response.data.errors)
            .filter((value) => Array.isArray(value))
            .flat();
          toastr.error(validationErrors.length > 0 ? validationErrors.join(", ") : extractErrorMessage(err));
        } else {
          toastr.error(extractErrorMessage(err));
        }
      } finally {
        setLoading(false);
      }
    },
  });

  const statusOptions = [
    { value: "true", label: "Active" },
    { value: "false", label: "Inactive" },
  ];

  return (
    <div className="page-content rp-page-content">
      <Container fluid>
        <div className="rp-page">
          <div className="rp-page-header">
            <h2 className="rp-page-header__title">
              {mode === "create" ? "Create Points Redemption Tier" : "Edit Points Redemption Tier"}
            </h2>
            <p className="rp-page-header__subtitle">
              {mode === "create" ? "Add a new points redemption tier" : "Update points redemption tier information"}
            </p>
          </div>

          <Form onSubmit={formik.handleSubmit}>
            <Row>
              <Col sm={12}>
                <CustomInput
                  id="name"
                  label="Name"
                  name="name"
                  type="text"
                  placeholder="Enter tier name (e.g., 10$ Voucher)"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.name && !!formik.errors.name}
                  errorText={formik.errors.name}
                  className="white-bg"
                />
              </Col>
            </Row>

            <Row>
              <Col xl={6} lg={6} md={12} sm={12}>
                <CustomInput
                  id="amount"
                  label="Amount ($)"
                  name="amount"
                  type="text"
                  placeholder="Enter amount (e.g., 10.00)"
                  value={formik.values.amount}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.amount && !!formik.errors.amount}
                  errorText={formik.errors.amount}
                  className="white-bg"
                />
              </Col>
              <Col xl={6} lg={6} md={12} sm={12}>
                <CustomInput
                  id="points_required"
                  label="Points Required"
                  name="points_required"
                  type="number"
                  placeholder="Enter points required"
                  value={formik.values.points_required}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.points_required && !!formik.errors.points_required}
                  errorText={formik.errors.points_required}
                  className="white-bg"
                />
              </Col>
            </Row>

            <Row>
              <Col xl={6} lg={6} md={12} sm={12}>
                <CustomSelector
                  id="is_active"
                  label="Status"
                  name="is_active"
                  value={formik.values.is_active}
                  onChange={(value) => formik.setFieldValue("is_active", value)}
                  options={statusOptions}
                  placeholder="Select status"
                  className="white-bg"
                />
              </Col>
              <Col xl={6} lg={6} md={12} sm={12}>
                <CustomInput
                  id="sort_order"
                  label="Sort Order"
                  name="sort_order"
                  type="number"
                  placeholder="Enter sort order"
                  value={formik.values.sort_order}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  invalid={formik.touched.sort_order && !!formik.errors.sort_order}
                  errorText={formik.errors.sort_order}
                  className="white-bg"
                />
              </Col>
            </Row>

              <div className="mt-2">
                <SecondaryButton
                  type="submit"
                  variant="orange"
                  disabled={loading}
                  loading={loading}
                  icon={tickIcon}
                  iconPosition="right"
                >
                  {loading ? "Saving..." : mode === "create" ? "Create Tier" : "Update Tier"}
                </SecondaryButton>
                <SecondaryButton
                  type="button"
                  variant="light-grey"
                  onClick={() => navigate("/admin/points-redemption-tiers")}
                  icon={cancelIcon}
                  iconPosition="right"
                  style={{ marginLeft: "10px" }}
                >
                  Cancel
                </SecondaryButton>
              </div>
            </Form>
        </div>
      </Container>
    </div>
  );
};

export default AdminPointsRedemptionTierCreatePage;
